import { CloudAnomalyAlert } from "../../types/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../../ui/common-ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../../ui/common-ui/collapsible";
import { JsonViewer } from "../../../../ui/common-ui/json-viewer";
import {
  AlertTriangle,
  User,
  Cloud,
  Copy,
  CheckCircle2,
  Brain,
  BookOpen,
  Route,
  FileText,
  Network,
  Activity,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Clock,
  Hash,
  Building2,
  ShieldCheck,
  Target,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { useToast } from "../../hooks/use-toast";
import "../../../ManagementDialog.css";
import "./AlertDetails.css";

interface AlertDetailsProps {
  alert: CloudAnomalyAlert | null;
  open: boolean;
  onClose: () => void;
}

const getSeverityConfig = (severity: string) => {
  const normalized = severity.toLowerCase();
  const configs: Record<string, { className: string; icon: typeof AlertTriangle; label: string }> = {
    critical: { className: "severity-critical", icon: AlertTriangle, label: "Critical" },
    high: { className: "severity-high", icon: AlertTriangle, label: "High" },
    medium: { className: "severity-medium", icon: AlertTriangle, label: "Medium" },
  };
  return configs[normalized] || configs.medium;
};

const getAlertTypeConfig = (alertType: string) => {
  const configs = {
    BEHAVIOUR: { icon: Brain, label: "Behaviour", className: "bg-purple-100 text-purple-700 border-purple-300" },
    RULE: { icon: BookOpen, label: "Rule", className: "bg-blue-100 text-blue-700 border-blue-300" },
    SEQUENTIAL: { icon: Route, label: "Sequential", className: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  };
  return configs[alertType as keyof typeof configs] || configs.RULE;
};

const getActionIcon = (action?: string) => {
  const a = (action ?? "").toLowerCase();
  if (a.includes("iam") || a.includes("serviceaccount")) return User;
  if (a.includes("storage") || a.includes("bucket")) return FileText;
  if (a.includes("network") || a.includes("firewall")) return Network;
  return Activity;
};

const getLogTime = (logData: any): string | undefined => {
  return (
    logData?.timestamp ||
    logData?.eventTime ||
    logData?.time ||
    logData?.protoPayload?.request_metadata?.request_attributes?.time ||
    logData?.protoPayload?.requestMetadata?.requestAttributes?.time
  );
};

const safeFormatTimestamp = (time?: string) => {
  if (!time) return undefined;
  const d = new Date(time);
  if (!isValid(d)) return undefined;
  return format(d, "yyyy-MM-dd HH:mm:ss");
};

const inferStepFromLog = (logData: any) => {
  const action =
    logData?.protoPayload?.method_name ||
    logData?.protoPayload?.methodName ||
    logData?.eventName ||
    logData?.operationName;

  const resource =
    logData?.protoPayload?.resource_name ||
    logData?.protoPayload?.resourceName ||
    logData?.protoPayload?.authorization_info?.[0]?.resource ||
    logData?.protoPayload?.authorizationInfo?.[0]?.resource ||
    logData?.resourceName ||
    logData?.resource?.type;

  const ip =
    logData?.protoPayload?.request_metadata?.caller_ip ||
    logData?.protoPayload?.requestMetadata?.callerIp ||
    logData?.sourceIPAddress;

  const region =
    logData?.resource?.labels?.region ||
    logData?.resource?.labels?.zone ||
    logData?.awsRegion ||
    logData?.region ||
    logData?.protoPayload?.resource_location?.current_locations?.[0] ||
    logData?.protoPayload?.resourceLocation?.currentLocations?.[0];

  const time = getLogTime(logData);

  return {
    action: action ?? "Unknown action",
    resource: resource ?? "—",
    ip,
    region,
    time,
  };
};

const buildTimelineSteps = (alert: CloudAnomalyAlert) => {
  const attackNums = Object.keys(alert.attackPath ?? {}).map(Number);
  const logNums = Object.keys(alert.logs ?? {}).map(Number);

  const steps = Array.from(new Set([...attackNums, ...logNums]))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  return steps.map((step) => {
    const ap = (alert.attackPath as any)?.[step];
    const logData = (alert.logs as any)?.[step];
    const inferred = logData ? inferStepFromLog(logData) : undefined;

    return {
      step,
      action: ap?.action ?? inferred?.action ?? `Step ${step}`,
      resource: ap?.resource ?? inferred?.resource ?? "—",
      ip: ap?.ip ?? inferred?.ip,
      region: ap?.region ?? inferred?.region,
      time: ap?.time ?? inferred?.time,
      logData,
    };
  });
};

export const AlertDetails = ({ alert, open, onClose }: AlertDetailsProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [fullyExpandedLogs, setFullyExpandedLogs] = useState<Set<number>>(new Set());
  const { toast } = useToast();

  if (!alert) return null;

  const severityConfig = getSeverityConfig(alert.severity);
  const alertTypeConfig = getAlertTypeConfig(alert.alertType);
  const SeverityIcon = severityConfig.icon;
  const AlertTypeIcon = alertTypeConfig.icon;

  const timelineSteps = buildTimelineSteps(alert);
  const mitreEntries = Object.entries(alert.mitre);
  const hasTimelineView = alert.alertType === "SEQUENTIAL" || alert.alertType === "RULE";
  const hasTimelineSteps = timelineSteps.length > 0;

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast({
        title: "Copied to clipboard",
        description: `${fieldName} has been copied to your clipboard.`,
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Unable to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  const toggleLogExpanded = (step: number) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      return next;
    });
  };

  const toggleFullyExpandedLog = (step: number) => {
    setFullyExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      return next;
    });
  };

  const CopyButton = ({ text, fieldName }: { text: string; fieldName: string }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, fieldName)}
      className="cad-copy"
      title={`Copy ${fieldName}`}
      aria-label={`Copy ${fieldName}`}
    >
      {copiedField === fieldName ? (
        <CheckCircle2 className="text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy />
      )}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="management-dialog cad-dialog sm:max-w-5xl" data-severity={severityConfig.label}>
        <DialogHeader className="management-dialog-header cad-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon cad-header-icon" aria-hidden="true">
              <SeverityIcon />
            </span>
            <div className="min-w-0">
              <div className="cad-chip-row">
                <span className="cad-chip" data-tone="type">
                  <AlertTypeIcon aria-hidden="true" />
                  {alertTypeConfig.label}
                </span>
                <span className="cad-chip" data-tone="severity">
                  <SeverityIcon aria-hidden="true" />
                  {severityConfig.label}
                </span>
                {hasTimelineView && hasTimelineSteps && (
                  <span className="cad-chip" data-tone="muted">
                    <Route aria-hidden="true" />
                    {timelineSteps.length} attack step{timelineSteps.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <DialogTitle className="cad-title">{alert.title}</DialogTitle>
              <DialogDescription className="cad-subtitle">
                Created {format(new Date(alert.createdAt), "MMM dd, yyyy · HH:mm:ss")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="management-dialog-body cad-body">
          <div className="cad-overview">
            <div className="cad-field cad-field-wide">
              <span className="cad-field-icon" aria-hidden="true"><User /></span>
              <div className="min-w-0 flex-1">
                <span className="cad-label">Entity</span>
                <div className="cad-value-row">
                  <span className="cad-value" title={alert.entity}>{alert.entity}</span>
                  <CopyButton text={alert.entity} fieldName="Entity" />
                </div>
              </div>
            </div>
            <div className="cad-field">
              <span className="cad-field-icon" aria-hidden="true"><Cloud /></span>
              <div className="min-w-0 flex-1">
                <span className="cad-label">Cloud Provider</span>
                <span className="cad-value">{alert.cloudType}</span>
              </div>
            </div>
            <div className="cad-field">
              <span className="cad-field-icon" aria-hidden="true"><Hash /></span>
              <div className="min-w-0 flex-1">
                <span className="cad-label">Cloud Account ID</span>
                <div className="cad-value-row">
                  <span className="cad-value cad-mono" title={alert.cloudAccountId}>{alert.cloudAccountId}</span>
                  <CopyButton text={alert.cloudAccountId} fieldName="Account ID" />
                </div>
              </div>
            </div>
            <div className="cad-field">
              <span className="cad-field-icon" aria-hidden="true"><Building2 /></span>
              <div className="min-w-0 flex-1">
                <span className="cad-label">Tenant</span>
                <span className="cad-value" title={alert.tenantName}>{alert.tenantName}</span>
              </div>
            </div>
          </div>

          {hasTimelineView && (
            <section className="cad-section">
              <h3 className="cad-section-title">
                <Route aria-hidden="true" />
                Attack Timeline
              </h3>

              {hasTimelineSteps ? (
                <ol className="cad-timeline" data-multi={timelineSteps.length > 1}>
                  {timelineSteps.map((stepData, index) => {
                    const step = stepData.step;
                    const ActionIcon = getActionIcon(stepData.action);
                    const isFirst = index === 0;
                    const isLast = index === timelineSteps.length - 1;
                    const logData = stepData.logData;
                    const isLogExpanded = expandedLogs.has(step);
                    const stepTime = safeFormatTimestamp(stepData.time);
                    const position = timelineSteps.length > 1 ? (isFirst ? "first" : isLast ? "last" : "middle") : "single";

                    return (
                      <li key={step} className="cad-step" data-position={position}>
                        <span className="cad-step-marker">{step}</span>
                        <div className="cad-step-card">
                          <div className="cad-step-head">
                            <span className="cad-step-icon" aria-hidden="true"><ActionIcon /></span>
                            <span className="cad-step-action">{stepData.action}</span>
                            {stepTime && (
                              <span className="cad-step-time">
                                <Clock aria-hidden="true" />
                                {stepTime}
                              </span>
                            )}
                          </div>

                          <div className="cad-step-grid">
                            <div className="cad-step-field cad-step-field-wide">
                              <span className="cad-label">Resource</span>
                              <span className="cad-mono cad-break">{stepData.resource}</span>
                            </div>
                            {stepData.ip && (
                              <div className="cad-step-field">
                                <span className="cad-label">IP Address</span>
                                <span className="cad-mono">{stepData.ip}</span>
                              </div>
                            )}
                            {stepData.region && (
                              <div className="cad-step-field">
                                <span className="cad-label">Region</span>
                                <span>{stepData.region}</span>
                              </div>
                            )}
                          </div>

                          {logData && (
                            <Collapsible open={isLogExpanded} onOpenChange={() => toggleLogExpanded(step)}>
                              <div className="cad-log-bar">
                                <CollapsibleTrigger className="cad-log-trigger">
                                  {isLogExpanded ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                                  View Raw Log
                                </CollapsibleTrigger>
                                {isLogExpanded && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFullyExpandedLog(step);
                                    }}
                                    className="cad-log-toggle"
                                  >
                                    {fullyExpandedLogs.has(step) ? (
                                      <>
                                        <Minimize2 aria-hidden="true" />
                                        Collapse
                                      </>
                                    ) : (
                                      <>
                                        <Maximize2 aria-hidden="true" />
                                        Expand
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                              <CollapsibleContent className="pt-3">
                                <JsonViewer
                                  data={logData}
                                  defaultExpanded={false}
                                  forceExpandAll={fullyExpandedLogs.has(step) ? true : undefined}
                                  className="cad-json"
                                />
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="cad-empty">No attack path data available for this alert.</div>
              )}
            </section>
          )}

          {(alert.info.why || alert.info.what || alert.info.how) && (
            <section className="cad-section">
              <h3 className="cad-section-title">
                <Brain aria-hidden="true" />
                Analysis
              </h3>
              <div className="cad-analysis">
                {alert.info.why && (
                  <div className="cad-analysis-card" data-tone="why">
                    <span className="cad-analysis-icon" aria-hidden="true"><AlertTriangle /></span>
                    <h4 className="cad-analysis-title">Why this matters</h4>
                    <p className="cad-analysis-text">{alert.info.why}</p>
                  </div>
                )}
                {alert.info.what && (
                  <div className="cad-analysis-card" data-tone="what">
                    <span className="cad-analysis-icon" aria-hidden="true"><Activity /></span>
                    <h4 className="cad-analysis-title">What happened</h4>
                    <p className="cad-analysis-text">{alert.info.what}</p>
                  </div>
                )}
                {alert.info.how && (
                  <div className="cad-analysis-card" data-tone="how">
                    <span className="cad-analysis-icon" aria-hidden="true"><ShieldCheck /></span>
                    <h4 className="cad-analysis-title">Recommended actions</h4>
                    <p className="cad-analysis-text">{alert.info.how}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          <div className="cad-split">
            {mitreEntries.length > 0 && (
              <section className="cad-section cad-panel">
                <h3 className="cad-section-title">
                  <Target aria-hidden="true" />
                  MITRE ATT&CK Techniques
                </h3>
                <div className="cad-mitre">
                  {mitreEntries.map(([code, name]) => (
                    <span key={code} className="cad-mitre-chip" title={name}>
                      <span className="cad-mitre-code">{code}</span>
                      {name}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <section className="cad-section cad-panel">
              <h3 className="cad-section-title">
                <Clock aria-hidden="true" />
                Detection Timeline
              </h3>
              <div className="cad-detection">
                <div className="cad-detection-item">
                  <span className="cad-label">Window Start</span>
                  <span className="cad-mono">{format(new Date(alert.timeRangeStart), "MMM dd, HH:mm:ss")}</span>
                </div>
                <div className="cad-detection-item">
                  <span className="cad-label">Window End</span>
                  <span className="cad-mono">{format(new Date(alert.timeRangeEnd), "MMM dd, HH:mm:ss")}</span>
                </div>
                <div className="cad-detection-item">
                  <span className="cad-label">Alert Created</span>
                  <span className="cad-mono">{format(new Date(alert.createdAt), "MMM dd, HH:mm:ss")}</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
